import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { PartialVLF, ValidListFilterType } from '../types';
import {
  normalizeFiltersForDataType,
  normalizeFilterValue,
} from '../utility/filterUtils';
import { hasLength } from 'utility/functions';

/**
 * Setup and manage filters LOCALLY.
 */
export type UseLocalAdvancedFiltersConfig = {
  initialFilters?: PartialVLF;
  onApplyLocalState?: (filters: PartialVLF) => void;
  externalStagedFilters?: PartialVLF;
  externalSetStagedFilters?: React.Dispatch<React.SetStateAction<PartialVLF>>;
};

export type UseLocalAdvancedFiltersReturn = {
  stagedFilters: PartialVLF;
  setStagedFilters: React.Dispatch<React.SetStateAction<PartialVLF>>;
  updateStagedFilterValue: (
    key: keyof ValidListFilterType,
    value: string | string[],
  ) => void;
  applyFilters: () => void;
  resetFilters: () => void;
};

/**
 * Handles LOCAL state management of filters.
 */
export function useLocalAdvancedFilters(
  config: UseLocalAdvancedFiltersConfig,
): UseLocalAdvancedFiltersReturn {
  const {
    initialFilters = {},
    onApplyLocalState,
    externalStagedFilters,
    externalSetStagedFilters,
  } = config;

  // Stringify initial filters for deep comparison to detect value changes
  const initialFiltersJson = useMemo(() => {
    const json = JSON.stringify(initialFilters);
    return json;
  }, [initialFilters]);

  // Normalize initial filters to ensure correct data types for multi-select fields
  const normalizedInitialFilters = useMemo(() => {
    const parsed = JSON.parse(initialFiltersJson) as PartialVLF;
    return normalizeFiltersForDataType(parsed);
  }, [initialFiltersJson]);

  // Use external state if provided, otherwise use internal state
  const [internalStagedFilters, setInternalStagedFilters] = useState<PartialVLF>(
    normalizedInitialFilters,
  );

  // When external staged filters are provided, use them directly; otherwise use internal state
  const stagedFilters = externalStagedFilters ?? internalStagedFilters;
  const setStagedFilters = externalSetStagedFilters ?? setInternalStagedFilters;

  // Keep a ref of the latest staged filters so callers (e.g. applyFilters) can read the most recent value synchronously even if state hasn't flushed yet.
  const stagedFiltersRef = useRef<PartialVLF>(stagedFilters);

  // Keep the ref in sync with the state when it changes externally
  useEffect(() => {
    stagedFiltersRef.current = stagedFilters;
  }, [stagedFilters]);

  // When using external state, sync it with initialFilters only when external state is empty.
  // Only call the external setter with the concrete normalized filters (not a functional updater)
  // so tests receiving mock setters get the object value directly.
  useEffect(() => {
    if (!externalSetStagedFilters) {
      return;
    }

    const isExternalEmpty =
      !externalStagedFilters || Object.keys(externalStagedFilters).length === 0;
    if (isExternalEmpty) {
      externalSetStagedFilters(normalizedInitialFilters);
      stagedFiltersRef.current = normalizedInitialFilters;
    }
  }, [externalSetStagedFilters, externalStagedFilters, normalizedInitialFilters]);

  const updateStagedFilterValue = useCallback(
    (key: keyof ValidListFilterType, value: string | string[]) => {
      const normalizedValue = normalizeFilterValue(String(key), value);

      // Staged filters use the ref to set the next state
      // synchronously so we can rely on the updated value immediately
      const prev = stagedFiltersRef.current || {};

      let next: PartialVLF;
      if (
        normalizedValue === '' ||
        normalizedValue === null ||
        normalizedValue === undefined ||
        (Array.isArray(normalizedValue) && normalizedValue.length === 0)
      ) {
        const { [key]: _, ...rest } = prev as Record<string, any>;
        next = rest as PartialVLF;
      } else {
        next = {
          ...prev,
          [key]: normalizedValue,
        } as PartialVLF;
      }

      // Update the staged filters ref then update state
      stagedFiltersRef.current = next;
      setStagedFilters(() => next);
    },
    [setStagedFilters],
  );

  // Apply staged filters
  const applyFilters = useCallback(() => {
    if (onApplyLocalState) {
      // JS filters object via stagedFiltersRef.current
      // Values normalized in updateStagedFilterValue -> normalizeFilterValue
      // Clean up empty values before sending to onApplyLocalState
      const cleanedFilters: PartialVLF = {};
      Object.entries(stagedFiltersRef.current).forEach(([key, value]) => {
        if (!hasLength(value)) {
          return;
        }
        cleanedFilters[key] = value;
      });
      onApplyLocalState(cleanedFilters);
    }
  }, [onApplyLocalState]);

  const resetFilters = useCallback(() => {
    setStagedFilters({});
  }, [setStagedFilters]);

  return {
    stagedFilters,
    setStagedFilters,
    updateStagedFilterValue,
    applyFilters,
    resetFilters,
  };
}
